import { useRef } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Grid, Dialog, Container, Stack, Button, Box } from '@mui/material';
import { AddReminder } from '../../sections/@dashboard/leadDetail';
// V2 task creation dialog — used when `tasks-v2` permission is view/manage
// (§9 MUST FE — single highest-leverage chokepoint; this swap kills V1 task
// creation across global +, Tasks tab "New Task", bulk dialogs, etc.)
import CreateTaskDialog from '../../sections/tasks/CreateTaskDialog';
import useFeaturePermission from '../../hooks/useFeaturePermission';
import { useSelector } from '../../redux/store';
import { Transition } from '.';
import {
    reminderDialogStyle,
    addReminderStackStyle,
    reminderFooterStyle,
    addReminderCancelButtonStyle,
    addRemindersaveButtonStyle} from '../../styles/AddReminderPopup';

const AddReminderPopup = ({ open, onClose, leadDetail, type, leadData, reload, reloadTaskCounter, page, handleLoading, onSuccess, defaultTitle, defaultDescription }) => {
    const inputRef = useRef();

    // §9 MUST FE — gate on `tasks-v2`. When the flag is view/manage for the
    // current practice render the V2 CreateTaskDialog; otherwise fall through
    // to the legacy <AddReminder>. Existing callbacks (onClose / reload) stay
    // intact so callers do not need to change.
    const { canView: hasTasksV2 } = useFeaturePermission('tasks-v2');
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const handleNext = async () => {
        await inputRef.current.submit();
    };

    if (hasTasksV2) {
        // Best-effort defaultLead: prefer the explicit leadDetail object,
        // fall back to leadData (used by some bulk callers).
        const defaultLead = leadDetail || leadData || null;
        const handleSuccess = () => {
            // Mirror the V1 reload signal so calling surfaces refresh counters/lists.
            if (typeof reload === 'function') reload();
            if (typeof reloadTaskCounter === 'function') reloadTaskCounter();
            // Notify the checklist (or any other caller) that the task was created.
            if (typeof onSuccess === 'function') onSuccess();
        };
        return (
            <CreateTaskDialog
                open={open}
                onClose={onClose}
                practiceId={practiceId}
                defaultLead={defaultLead}
                onSuccess={handleSuccess}
                defaultTitle={defaultTitle || null}
                defaultDescription={defaultDescription || null}
            />
        );
    }

    return (
        <Dialog
            TransitionComponent={Transition}
            open={open}
            sx={reminderDialogStyle}
        >
            <Stack sx={{ ...addReminderStackStyle, overflow: 'hidden', padding: '0' }}>
                <Grid container spacing={0} sx={{ margin: 'auto', width: '100%' }}>
                    <Container maxWidth={'xl'} sx={{ padding: '0 !important' }}>
                        <AddReminder ref={inputRef} handleNext={onClose} onSaved={onSuccess} reloadTaskCounter={reloadTaskCounter} page={page} type={type} leadDetail={leadDetail} leadData={leadData} reload={reload} handleLoading={handleLoading} />
                    </Container>
                </Grid>
            </Stack>
            <Box sx={reminderFooterStyle}>
                <Button
                    color='inherit'
                    variant='outlined'
                    sx={addReminderCancelButtonStyle}
                    onClick={() => onClose(true)}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    onClick={handleNext}
                    sx={addRemindersaveButtonStyle}
                >
                    {type === 'editTask' ? 'Save changes' : 'Save task'}
                </Button>
            </Box>
        </Dialog>
    );
};

export default AddReminderPopup;

AddReminderPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    leadDetail: PropTypes.object,
    type: PropTypes.string,
    leadData: PropTypes.object,
    reload: PropTypes.bool,
    reloadTaskCounter: PropTypes.bool,
    page: PropTypes.string,
    handleLoading: PropTypes.func,
    onSuccess: PropTypes.func,
    defaultTitle: PropTypes.string,
    defaultDescription: PropTypes.string,
};